import Observable from '@speechki-frontend/shared/Observable';
import { makeid } from '@speechki-frontend/shared/helpers/functions';
import { DEFAULT_BUILDER_PERMISSIONS, DEFAULT_BUILD_OPTIONS } from './utils';

const createConfig = (schema, config = {}) => ({
    ...DEFAULT_BUILD_OPTIONS,
    id: makeid(),
    providers: (schema.providers || {}).multiple ? [] : null,
    build_types: [],
    options: 'fullText',
    $valid: false,
    $completed: false,
    ...config
});

const createSchema = schema => {
    if (Array.isArray(schema)) return schema.map(k => [k, {}]);
    if (schema instanceof Object) return Object.entries(schema);
    return {};
};

let TIMER = 0;

export default class BuilderFactory extends Observable {
    constructor(builder, context) {
        super();

        if (!(builder instanceof Object)) throw new Error('builder must be defined');
        if (!(context instanceof Object)) throw new Error('context must be defined');

        const { api } = builder;

        this._builder = builder;
        this._context = context;
        this._wsEvent = api.ws.name;

        this.options = builder.options;
        this.configs = [];
        this.addConfig();

        this.on('completed', () => this.finish());
        this.on('finished', () => this.finish());
    }
    get name() {
        return this._builder.name;
    }
    get permissions() {
        const { permissions = {} } = this._builder;
        return { ...DEFAULT_BUILDER_PERMISSIONS, ...permissions };
    }
    get api() {
        return new Proxy(
            { ...DEFAULT_BUILDER_PERMISSIONS },
            {
                get: (target, key) => {
                    if (!Object.prototype.hasOwnProperty.call(target, key)) {
                        return undefined;
                    }

                    const { [key]: value } = target;
                    if (!value) {
                        throw new Error('Invalid API method');
                    }

                    const { [key]: method } = this._builder.api;
                    const {
                        [this.permissions[key]]: permission = false
                    } = this._context.permissions;

                    if (!method) {
                        return false;
                    }
                    if (!permission) {
                        this.fire('denied', { key });
                        return false;
                    }

                    return method.bind(this._context);
                }
            }
        );
    }
    get schema() {
        const newSchema = createSchema(this._builder.schema);
        return Object.fromEntries(
            newSchema.map(([k, options]) => [k, { ...this._context.schema[k], ...options }])
        );
    }
    get bookId() {
        return this._context.bookId;
    }
    get canAdd() {
        return this.options.configsLimit > this.configs.length;
    }
    get canDelete() {
        return this.configs.length > 1;
    }
    get isValid() {
        return this.configs.every(({ $valid }) => $valid);
    }
    subscribeEvents(context) {
        const { handlers = {} } = this._builder;
        for (const [event, handler] of Object.entries(handlers)) {
            this.on(event, handler.bind(context));
        }
    }
    getPermission(name) {
        const { [name]: key = null } = this.permissions;
        const { [key]: result = null } = this._context.permissions;

        if (result === null) throw new Error(`Invalid permission key "${name}"`);
        return result;
    }
    validateConfigs() {
        this.configs = this.configs.map(config => ({ ...config, validated: true }));
    }
    addConfig(values = {}) {
        let warning = null;

        this.validateConfigs();

        if (!this.canAdd) warning = 'Cannot be added';
        if (!this.isValid) warning = 'Validation failed';
        if (warning) return this.fire('warning', { title: warning, values });

        this.configs.push(createConfig(this.schema, values));
    }
    deleteConfig(id) {
        const newConfigs = this.configs.filter(c => c.id !== id);
        this.configs = newConfigs;
    }
    _wsHandler(event) {
        const { code, errors, params } = event;
        const validate = this._builder.api.ws.validator.bind(this._context);

        if (code !== 200 || errors) {
            return this.fire('failed', { error: '_wsHandler', event, errors });
        }

        if (!validate(params)) {
            return this.fire('failed', { error: 'api.ws.validator' });
        }

        if (this._updateConfigs(params)) {
            this.fire('completed', event);
        }
    }
    _updateConfigs({ provider }) {
        const config = this.configs.find(({ providers }) => providers === provider);

        if (config) {
            config.$completed = true;
        }

        return !this.configs.some(({ $completed }) => !$completed);
    }
    fetch() {
        const { fetch } = this.api;
        if (!fetch) return [];
        return fetch(this.configs);
    }
    building() {
        const { building } = this.api;

        if (!building) return false;
        if (this._wsEvent) {
            this._context.ws.on(this._wsEvent, this._wsHandler, this);
        }
        return building(this.configs);
    }
    queueChecking() {
        const { checking } = this.api;
        let { delay = 0, retries = 0 } = this.options.queueChecking;

        if (!(checking && retries && delay)) return false;

        TIMER = setInterval(async () => {
            try {
                const { in_queue = null } = await checking();

                if (in_queue === false) this.fire('completed');
                if (!retries) this.fire('finished');
            } catch (error) {
                console.log(error);
            }
        }, delay);
    }
    finish() {
        this._context.ws.off(this._wsEvent, this._wsHandler, this);
        clearTimeout(TIMER);
    }
    destroy() {
        this.finish();
        this.offAll();
    }
}
